import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  fetchTrackAdGroups,
  fetchTrackCampaigns,
  fetchTrackEvents,
  fetchTrackInfo,
  fetchTrackServices,
  reset,
  setLinkTab,
  setUsageTab,
} from 'features/crux/tracker/detail/trackerDetailSlice'
import { useHistory, useParams } from 'react-router-dom'
import Moment from 'react-moment'
import { dateformat, isNullOrUndefined } from 'utils'
import classNames from 'classnames'
import Button from 'components/form/Button'
import HelpTooltip from 'components/tooltip/HelpTooltip'
import { fail, success } from 'features/toast/Toast'
import { useConfirm } from 'features/dialog/Confirm'
import NoneAnchor from '../../../../components/NoneAnchor'
import Services from 'features/crux/tracker/detail/items/Services'
import Events from 'features/crux/tracker/detail/items/Events'
import Campaigns from 'features/crux/tracker/detail/items/Campaigns'
import AdGroups from 'features/crux/tracker/detail/items/AdGroups'
import {
  linkTrackRight,
  unlinkTrackRight,
} from 'features/crux/tracker/list/trackerListSlice'
import {
  cancelTrackMember,
  requestTrackMember,
} from 'features/crux/tracker/modal/request/requestTrackSlice'

const { REACT_APP_BUSINESS_HOST } = process.env
const TrackerDetail = () => {
  const dispatch = useDispatch()
  const { accountId: adAccountId } = useParams()
  const {
    info: {
      trackId: id,
      name,
      population,
      role,
      createdAt,
      lastEventTs,
      adAccountLinkStatus,
      requestId,
    },
    loaded,
    isEmpty,
    isLoading,
    isSubmitting,
    linkTab,
    usageTab,
    services,
    events,
    campaigns,
    adGroups,
  } = useSelector(state => state.tracker.detail)
  const { trackId } = useParams()
  const history = useHistory()
  const [LinkConfirm, showLinkConfirm] = useConfirm()
  const [UnlinkConfirm, showUnlinkConfirm] = useConfirm()
  const showPopulation =
    role === 'MASTER' || role === 'MEMBER' || adAccountLinkStatus === 'LINKED'

  useEffect(() => {
    if (isEmpty) {
      fail('해당 픽셀 & SDK가 존재하지 않습니다.')
      history.push(`/${adAccountId}/tracker`)
    }
  }, [isEmpty, history, adAccountId])
  useEffect(() => {
    if (trackId) {
      dispatch(fetchTrackInfo(trackId))
    }
  }, [dispatch, trackId])
  useEffect(() => {
    if (trackId && loaded) {
      dispatch(fetchTrackServices(trackId))
      dispatch(fetchTrackEvents(trackId))
    }
  }, [dispatch, trackId, loaded])
  useEffect(() => {
    if (trackId && loaded) {
      dispatch(fetchTrackCampaigns(trackId))
    }
  }, [dispatch, trackId, loaded, campaigns.page])
  useEffect(() => {
    if (trackId && loaded) {
      dispatch(fetchTrackAdGroups(trackId))
    }
  }, [dispatch, trackId, loaded, adGroups.page])
  useEffect(
    () => () => {
      dispatch(reset())
    },
    [dispatch]
  )

  const handleRequestTrackMember = async () => {
    const { error, payload } = await dispatch(requestTrackMember(trackId))
    if (error) {
      fail(payload?.message || '알 수 없는 에러가 발생했습니다.')
    } else {
      dispatch(fetchTrackInfo(trackId))
      success('멤버 요청이 완료되었습니다.')
    }
  }

  const handleCancelTrackMember = async () => {
    const { error, payload } = await dispatch(
      cancelTrackMember({ trackId, requestId })
    )
    if (error) {
      fail(payload?.message || '알 수 없는 에러가 발생했습니다.')
    } else {
      dispatch(fetchTrackInfo(trackId))
      success('멤버 요청을 취소하였습니다.')
    }
  }

  return (
    <>
      <div className="pixelsdkdetail_wrap linkagedetail_wrap">
        <div className="dsp_tit">
          <div className="tit_dsp">
            <h3 className="tit_subject">픽셀&SDK 연동 상세</h3>
            <strong className="sub_title">{name}</strong>
            <span className="sub_code">{id}</span>
            {adAccountLinkStatus === 'UNLINKED' && (
              <Button round>연동 해제됨</Button>
            )}
            <div className="f_right">
              <div className="single_wrap">
                {adAccountLinkStatus === 'LINKED' ? (
                  <Button
                    line
                    onClick={showUnlinkConfirm}
                    disabled={isSubmitting || isLoading}>
                    연동 해제
                  </Button>
                ) : (
                  <Button
                    line
                    onClick={showLinkConfirm}
                    disabled={
                      isSubmitting ||
                      isLoading ||
                      role === 'REQUEST' ||
                      isNullOrUndefined(role)
                    }>
                    연동 하기
                  </Button>
                )}
              </div>
              <div className="single_wrap">
                <Button
                  line
                  linkTo={`/${adAccountId}/tracker${
                    adAccountLinkStatus !== 'LINKED' ? '?tab=unlinked' : ''
                  }`}>
                  목록
                </Button>
              </div>
            </div>
          </div>
        </div>
        <div className="ad_managebox">
          <div className="managebox_tit">
            <strong className="tit_box">기본 정보</strong>
            <div className="f_right">
              <Button
                line
                disabled={role !== 'MASTER' && role !== 'MEMBER'}
                onClick={() => {
                  window.open(
                    `${REACT_APP_BUSINESS_HOST}/pixel/detail/${trackId}`
                  )
                }}>
                픽셀 &amp; SDK 관리
              </Button>
            </div>
          </div>
          <table className="tbl_g3">
            <caption className="hide_caption">
              픽셀 &amp; SDK 기본정보 표
            </caption>
            <colgroup>
              <col style={{ width: 200 }} />
              <col style={{ width: 510 }} />
              <col style={{ width: 200 }} />
              <col />
            </colgroup>
            <tbody>
              <tr>
                <th scope="row">
                  <div className="inner_tbl">픽셀 &amp; SDK 이름</div>
                </th>
                <td colSpan="3">
                  <div className="inner_tbl">{name}</div>
                </td>
              </tr>
              <tr>
                <th scope="row">
                  <div className="inner_tbl">ID</div>
                </th>
                <td>
                  <div className="inner_tbl">{id}</div>
                </td>
                <th scope="row">
                  <div className="inner_tbl">모수</div>
                </th>
                <td>
                  <div className="inner_tbl">
                    {lastEventTs
                      ? showPopulation
                        ? population.toLocaleString()
                        : '미제공(권한 없음)'
                      : '-'}
                  </div>
                </td>
              </tr>
              <tr>
                <th scope="row">
                  <div className="inner_tbl">
                    {adAccountLinkStatus === 'LINKED'
                      ? '광고계정 연동 일시'
                      : '생성일시'}
                  </div>
                </th>
                <td>
                  <div className="inner_tbl">
                    {createdAt && (
                      <Moment format={dateformat}>{createdAt}</Moment>
                    )}
                  </div>
                </td>
                <th scope="row">
                  <div className="inner_tbl">이벤트 수집 현황</div>
                </th>
                <td>
                  <div className="inner_tbl">
                    <p
                      className={classNames('desc_state', {
                        on: !isNullOrUndefined(lastEventTs),
                        off: isNullOrUndefined(lastEventTs),
                      })}>
                      <span className="ico_state" />
                      {isNullOrUndefined(lastEventTs) ? '수집 전' : '수집 중'}
                    </p>
                  </div>
                </td>
              </tr>
              <tr>
                <th scope="row">
                  <div className="inner_tbl">내 권한</div>
                </th>
                <td>
                  <div className="inner_tbl">
                    {role === 'REQUEST' && (
                      <>
                        멤버 요청중
                        <Button
                          line
                          disabled={isSubmitting || isLoading}
                          onClick={handleCancelTrackMember}>
                          취소
                        </Button>
                      </>
                    )}
                    {role === 'MASTER' && '마스터'}
                    {role === 'MEMBER' && '멤버'}
                    {isLoading === false && isNullOrUndefined(role) && (
                      <>
                        권한 없음
                        <HelpTooltip>
                          ‘멤버 권한 요청’ 클릭 시, 픽셀 & SDK 마스터/멤버가
                          <br />
                          요청을 수락한 후 권한을 얻으면 픽셀 & SDK 관리를 할 수
                          있습니다.
                        </HelpTooltip>
                        <Button
                          line
                          disabled={isSubmitting || isLoading}
                          onClick={handleRequestTrackMember}>
                          멤버 권한 요청
                        </Button>
                      </>
                    )}
                  </div>
                </td>
                <th scope="row">
                  <div className="inner_tbl">마지막 이벤트 유입 시간</div>
                </th>
                <td>
                  <div className="inner_tbl">
                    {lastEventTs ? (
                      <Moment format="YYYY-MM-DD HH[h]">{lastEventTs}</Moment>
                    ) : (
                      '-'
                    )}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="ad_managebox">
          <div className="managebox_tit">
            <strong className="tit_box">픽셀 &amp; SDK 연동 현황</strong>
          </div>
          <ul className="tab_g8" role="tablist">
            <li
              role="presentation"
              className={classNames({ on: linkTab === 'services' })}>
              <NoneAnchor
                role="tab"
                className="link_tab"
                onClick={() => dispatch(setLinkTab('services'))}>
                연동 서비스{' '}
                <em className="fc_emph">{services.totalElements}</em>
              </NoneAnchor>
            </li>
            <li
              role="presentation"
              className={classNames({ on: linkTab === 'events' })}>
              <NoneAnchor
                role="tab"
                className="link_tab"
                onClick={() => dispatch(setLinkTab('events'))}>
                수집 이벤트 <em className="fc_emph">{events.totalElements}</em>
              </NoneAnchor>
            </li>
          </ul>
          {linkTab === 'services' && <Services />}
          {linkTab === 'events' && <Events />}
        </div>
        <div className="ad_managebox">
          <div className="managebox_tit">
            <strong className="tit_box">픽셀 &amp; SDK 사용 현황</strong>
          </div>
          <ul className="tab_g8" role="tablist">
            <li
              role="presentation"
              className={classNames({ on: usageTab === 'campaigns' })}>
              <NoneAnchor
                role="tab"
                className="link_tab"
                onClick={() => dispatch(setUsageTab('campaigns'))}>
                캠페인 <em className="fc_emph">{campaigns.totalElements}</em>
              </NoneAnchor>
            </li>
            <li
              role="presentation"
              className={classNames({ on: usageTab === 'adGroups' })}>
              <NoneAnchor
                role="tab"
                className="link_tab"
                onClick={() => dispatch(setUsageTab('adGroups'))}>
                광고그룹 <em className="fc_emph">{adGroups.totalElements}</em>
              </NoneAnchor>
            </li>
          </ul>
          {usageTab === 'campaigns' && <Campaigns />}
          {usageTab === 'adGroups' && <AdGroups />}
        </div>
      </div>
      <LinkConfirm
        title="픽셀 & SDK 연동"
        onClose={async ({ isConfirmed }) => {
          if (isConfirmed) {
            const { error, payload } = await dispatch(linkTrackRight(trackId))
            if (error) {
              fail(payload?.message || '알 수 없는 에러가 발생했습니다.')
            } else {
              success('해당 광고계정과 픽셀 & SDK 연동이 완료되었습니다.')
            }
          }
        }}>
        광고계정에 해당 픽셀 & SDK를 연동하시겠습니까?
      </LinkConfirm>
      <UnlinkConfirm
        title="픽셀 & SDK 연동 해제"
        onClose={async ({ isConfirmed }) => {
          if (isConfirmed) {
            const { error, payload } = await dispatch(unlinkTrackRight(trackId))
            if (error) {
              fail(payload?.message || '알 수 없는 에러가 발생했습니다.')
            } else {
              success('해당 광고계정과 픽셀 & SDK 연동이 해제되었습니다.')
              history.push(`/${adAccountId}/tracker`)
            }
          }
        }}>
        광고계정에 연동된 픽셀 & SDK를 해제하시겠습니까?
      </UnlinkConfirm>
    </>
  )
}
export default TrackerDetail
